import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import SearchableSelect from '../components/SearchableSelect';
import BlockTreePreview from '../components/BlockTreePreview';
import FilterSelect, { useFilterCache } from '../components/FilterSelect';
import FilterColumnsPicker from '../components/FilterColumnsPicker';
import '../styles/ConfigPage.css';

function ConfigPage() {
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState([]);
  const [workItemTypes, setWorkItemTypes] = useState([]);
  const [levelWorkItemTypes, setLevelWorkItemTypes] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(true);
  // Tenant base URL (with the account segment), from /api/config/projects. Only used to build
  // the ↗ "Open in Orcanos" filter links; empty simply means no link is rendered.
  const [orcanosUrl, setOrcanosUrl] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    version_id: '',
    base_work_item: '',
    base_level_name: '',
    base_filter_id: '',
    base_orphan_filter_id: '',
    base_hidden_columns: [],   // L1 filter columns hidden in the grid/exports (display only)
    levels: [],
    name: `Trace Setup ${new Date().toLocaleString()}`,
    is_public: true,
    owner_only_edit: true
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Clone (edit mode only): open the panel, tweak nothing or everything, save it as a NEW panel.
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);
  const [collapsedBlocks, setCollapsedBlocks] = useState(() => new Set());
  const [dragBlockId, setDragBlockId] = useState(null);   // block being dragged
  const [dropParentId, setDropParentId] = useState(null); // block/'L1' it would land under
  const [activeBlockId, setActiveBlockId] = useState(null); // node highlighted in the tree picture
  const { filterCache, ensureFilters } = useFilterCache();
  const navigate = useNavigate();
  const { setupId } = useParams();
  const location = useLocation();
  // Return to wherever the wizard was opened from (e.g. the trace view), falling
  // back to the dashboard when there's no origin (direct link / new panel).
  const returnTo = location.state?.from || '/trace/dashboard';

  const toggleCollapse = (id) => {
    setCollapsedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const loadProjects = useCallback(async () => {
    try {
      const response = await fetch('/api/config/projects');
      const data = await response.json();

      if (response.ok && data.projects && data.projects.length > 0) {
        setOrcanosUrl(data.orcanos_url || '');
        const projectList = data.projects.map(p => ({
          id: p.project_id,
          name: p.project_name,
          version_id: p.version_id,
          version_label: p.version_label,
          item_types: p.item_types || []
        }));
        setProjects(projectList);
      } else {
        setError('No projects found. Make sure you logged in with valid Orcanos credentials.');
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects: ' + err.message);
    } finally {
      setLoadingProjects(false);
    }
  }, []);


  const loadPanelForEdit = useCallback(async (id) => {
    try {
      const response = await fetch(`/api/config/${id}`);
      const data = await response.json();

      if (response.ok) {
        // Parse levels if JSON string
        let levels = data.levels;
        if (typeof levels === 'string') {
          levels = JSON.parse(levels);
        }
        levels = levels || [];

        // Normalize to the block-tree shape. Legacy panels have no id/parent_id — read
        // them as a linear chain (each block hangs under the previous one).
        const isNewFormat = levels.some(l => l.id || l.parent_id);
        if (!isNewFormat) {
          let prevId = 'L1';
          levels = levels.map((l, i) => {
            const id = 'b' + (i + 1);
            const norm = { ...l, id, parent_id: prevId, level_num: i + 2 };
            prevId = id;
            return norm;
          });
        }

        setFormData({
          project_id: data.project_id,
          version_id: data.version_id,
          base_work_item: data.base_work_item,
          base_level_name: data.base_level_name || '',
          base_filter_id: data.base_filter_id,
          base_orphan_filter_id: data.base_orphan_filter_id || '',
          base_hidden_columns: data.base_hidden_columns || [],
          levels: levels || [],
          name: data.name,
          is_public: data.is_public,
          owner_only_edit: data.owner_only_edit !== false  // absent (legacy) => owner-only
        });

        // Initialize level work item types based on their projects
        const newLevelWorkItemTypes = {};
        levels.forEach((level, idx) => {
          const levelProject = projects.find(p => p.id === level.project_id);
          if (levelProject && levelProject.item_types) {
            newLevelWorkItemTypes[idx] = levelProject.item_types;
          }
        });
        setLevelWorkItemTypes(newLevelWorkItemTypes);

        setIsEditMode(true);
        // Deliberately does NOT touch `step`. It used to setStep(1) here, but this runs async:
        // if the load resolved (or re-ran) after the user had already clicked Next, it threw
        // them back to step 1 — the "click Next, it flickers, works the second time" bug.
        // `step` already starts at 1, so there is nothing to set.
      } else {
        setError('Failed to load panel');
      }
    } catch (err) {
      console.error('Error loading panel:', err);
      setError('Error loading panel: ' + err.message);
    }
  }, [projects]);

  const loadWorkItemTypes = useCallback((projectId) => {
    const project = projects.find(p => p.id === projectId);
    const types = project && project.item_types ? project.item_types : [];
    setWorkItemTypes(types);
  }, [projects]);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Load the panel for edit exactly ONCE per setupId. The effect's deps (projects, and the
  // callback that closes over them) change after mount, and StrictMode double-invokes it in
  // dev — without this guard the panel is fetched again and setFormData overwrites whatever
  // the user has already typed.
  const loadedSetupRef = useRef(null);
  useEffect(() => {
    if (setupId && projects.length > 0 && loadedSetupRef.current !== setupId) {
      loadedSetupRef.current = setupId;
      loadPanelForEdit(setupId);
    }
  }, [setupId, projects, loadPanelForEdit]);

  // Load work item types when project changes
  useEffect(() => {
    if (formData.project_id) {
      loadWorkItemTypes(formData.project_id);
    }
  }, [formData.project_id, loadWorkItemTypes]);

  const handleProjectChange = (project) => {
    setFormData({
      ...formData,
      project_id: project?.id || '',
      version_id: project?.version_id || '',
      base_work_item: ''
    });
  };

  const handleWorkItemChange = (e) => {
    const label = e.target.value;
    const code = getWorkItemCodeFromLabel(label, workItemTypes);
    setFormData({ ...formData, base_work_item: code });
  };

  // ---- Block-tree helpers (multi-block levels) ----
  // Each block links to its parent via `parent_id` ("L1" = the base level). A depth can
  // hold several sibling blocks; "add level below" makes a child, "add block at this
  // level" makes a sibling (same parent). `level_num` is the derived depth.
  const genBlockId = () => 'b' + Math.random().toString(36).slice(2, 9);

  const depthOfParent = (parentId, levels) => {
    if (!parentId || parentId === 'L1') return 1;
    const parent = levels.find(l => l.id === parentId);
    return parent ? depthOfParent(parent.parent_id, levels) + 1 : 1;
  };

  // Collect a block id and all of its descendants (for cascade remove).
  const collectSubtree = (id, levels) => {
    const ids = [id];
    levels.filter(l => (l.parent_id || 'L1') === id).forEach(child => {
      ids.push(...collectSubtree(child.id, levels));
    });
    return ids;
  };

  const handleAddBlock = (parentId) => {
    const depth = depthOfParent(parentId, formData.levels) + 1;
    if (depth > 6) {
      setError('Maximum depth is 6 levels.');
      return;
    }
    const newLevel = {
      id: genBlockId(),
      parent_id: parentId,
      level_num: depth,
      work_item_type: '',
      filter_id: '',
      orphan_filter_id: '',
      hidden_columns: [],
      must_have: true,
      project_id: formData.project_id,
      version_id: formData.version_id
    };
    setFormData({ ...formData, levels: [...formData.levels, newLevel] });
  };

  // ---- Drag & drop re-parenting (pointer events, NOT the HTML5 drag API) ----
  // The HTML5 drag API is deliberately not used here: the browser paints its own "ghost"
  // snapshot of the drag source, and a source that re-renders mid-drag can leave that ghost
  // stuck on the page — it showed up as a giant block card floating outside the wizard.
  // Pointer events give us the drop logic with no browser-painted image at all.
  const dropTargetRef = useRef(null);   // read on pointerup, where state would be stale
  const endDragRef = useRef(null);      // teardown, so unmount can't leave listeners behind
  const [dragPos, setDragPos] = useState(null);  // our own ghost follows the pointer

  useEffect(() => () => { if (endDragRef.current) endDragRef.current(); }, []);

  // A block is moved with everything under it; the array order never changes (levelWorkItemTypes
  // is keyed by array index), only `parent_id` and the derived `level_num`.
  const subtreeHeight = (id, levels) => {
    const kids = levels.filter(l => (l.parent_id || 'L1') === id);
    return kids.length ? 1 + Math.max(...kids.map(k => subtreeHeight(k.id, levels))) : 1;
  };

  const recomputeDepths = (levels) =>
    levels.map(l => ({ ...l, level_num: depthOfParent(l.parent_id || 'L1', levels) + 1 }));

  // A block can't land on itself, inside its own subtree, or where it would push past L6.
  const canDropOn = (blockId, targetParentId, levels) => {
    if (!blockId || !targetParentId) return false;
    const block = levels.find(l => l.id === blockId);
    if (!block) return false;
    if ((block.parent_id || 'L1') === targetParentId) return false; // already there
    if (collectSubtree(blockId, levels).includes(targetParentId)) return false;
    const newDepth = depthOfParent(targetParentId, levels) + 1;
    return newDepth + subtreeHeight(blockId, levels) - 1 <= 6;
  };

  const startBlockDrag = (level) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();   // no text selection, and no chance of a native drag starting
    const levels = formData.levels;
    const startX = e.clientX, startY = e.clientY;
    let moved = false;    // a click on the handle is not a drag until the pointer travels

    const onMove = (ev) => {
      if (!moved) {
        if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) < 5) return;
        moved = true;
        setDragBlockId(level.id);
      }
      // The innermost drop zone under the pointer wins; an invalid one simply isn't a target.
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const zone = el && el.closest('[data-drop-target]');
      const id = zone && zone.getAttribute('data-drop-target');
      const target = id && canDropOn(level.id, id, levels) ? id : null;
      dropTargetRef.current = target;
      setDropParentId(target);
      setDragPos({ x: ev.clientX, y: ev.clientY });
    };

    const finish = (commit) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      endDragRef.current = null;
      const target = dropTargetRef.current;
      dropTargetRef.current = null;
      setDragBlockId(null);
      setDropParentId(null);
      setDragPos(null);
      if (!commit || !moved || !target) return;
      setFormData(prev => ({
        ...prev,
        levels: recomputeDepths(
          prev.levels.map(l => (l.id === level.id ? { ...l, parent_id: target } : l))
        )
      }));
      setError('');
    };

    const onUp = () => finish(true);
    const onCancel = () => finish(false);
    const onKey = (ev) => { if (ev.key === 'Escape') finish(false); };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    endDragRef.current = () => finish(false);
  };

  const handleRemoveLevel = (index) => {
    const target = formData.levels[index];
    const toRemove = new Set(collectSubtree(target.id, formData.levels));
    setFormData({
      ...formData,
      levels: formData.levels.filter(l => !toRemove.has(l.id))
    });
  };

  const handleLevelChange = (index, field, value) => {
    const newLevels = [...formData.levels];

    if (field === 'project_id') {
      const project = value; // value is the selected project object (id + version)
      newLevels[index] = {
        ...newLevels[index],
        project_id: project?.id || '',
        version_id: project?.version_id || '',
        work_item_type: '',
        work_item_type_label: ''
      };
      // Load work item types for this level's project
      const types = project && project.item_types ? project.item_types : [];
      setLevelWorkItemTypes({ ...levelWorkItemTypes, [index]: types });
    } else if (field === 'work_item_type') {
      // Convert label to code — fall back to L1 types if level types not yet loaded
      const levelTypes = levelWorkItemTypes[index] || workItemTypes || [];
      const code = getWorkItemCodeFromLabel(value, levelTypes);
      // Keep the human label too — it's the ONLY signal that tells a Test Run from a
      // Test Case (both share code T_CASE), which drives the special Test Run hop.
      newLevels[index] = { ...newLevels[index], work_item_type: code, work_item_type_label: value };
    } else {
      const isFilterField = field === 'filter_id' || field === 'orphan_filter_id';
      const prev = newLevels[index];
      newLevels[index] = { ...prev, [field]: isFilterField ? parseInt(value) || '' : value };
      // A new filter has its own columns: start again from "show all".
      if (field === 'filter_id' && newLevels[index].filter_id !== prev.filter_id) {
        newLevels[index].hidden_columns = [];
      }
    }

    setFormData({ ...formData, levels: newLevels });
  };

  // The wire payload for both save and clone — built from whatever is on screen now.
  const buildPayload = (overrides = {}) => {
    // Optional orphan filters: '' must go to the API as null (Optional[int]), not ''
    const toIntOrNull = (v) => (v === '' || v == null ? null : parseInt(v) || null);
    const cleanLevels = formData.levels.map((l) => ({
      ...l,
      orphan_filter_id: toIntOrNull(l.orphan_filter_id),
      must_have: l.must_have !== false,  // absent (legacy / just-added) => required
    }));
    return {
      ...formData,
      levels: cleanLevels,
      base_filter_id: parseInt(formData.base_filter_id),
      base_orphan_filter_id: toIntOrNull(formData.base_orphan_filter_id),
      ...overrides,
    };
  };

  const isConfigComplete = () => (
    formData.project_id && formData.version_id && formData.base_work_item && formData.base_filter_id
  );

  const handleSave = async () => {
    if (!isConfigComplete()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = buildPayload();

      const url = isEditMode ? `/api/config/${setupId}` : '/api/config';
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        navigate(returnTo);
      } else {
        const data = await response.json();
        setError(data.detail || 'Save failed');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Clone = POST the settings currently on screen as a BRAND-NEW panel. It deliberately does
  // not PUT first, so the original is left exactly as it was on the server (any unsaved edits
  // in the form go into the copy only). The copy is owned by whoever cloned it.
  const handleClone = async () => {
    if (!isConfigComplete()) {
      setError('Please fill in all required fields before cloning');
      return;
    }
    const name = (cloneName || '').trim();
    if (!name) {
      setError('Give the copy a name');
      return;
    }

    setCloning(true);
    setError('');

    try {
      const response = await fetch('/api/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify(buildPayload({ name }))
      });

      if (response.ok) {
        // Land on the Dashboard so the new copy is visible; returnTo may point at the
        // ORIGINAL panel's matrix, which would read as "nothing happened".
        navigate('/trace/dashboard');
      } else {
        const data = await response.json();
        setError(data.detail || 'Clone failed');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setCloning(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Discard changes and go back?')) {
      navigate(returnTo);
    }
  };

  // Get project options for searchable select
  const projectOptions = projects.map(p => `${p.name} (v${p.version_label})`);
  const projectValueMap = {}; // Map display -> project (identity is id + version)
  projects.forEach((p) => {
    projectValueMap[`${p.name} (v${p.version_label})`] = p;
  });

  // Helper functions to map between work item codes and labels
  const getWorkItemLabels = (itemTypes) => (itemTypes || []).map(it => it.label || it.code);
  const getWorkItemCodeFromLabel = (label, itemTypes) => {
    const item = (itemTypes || []).find(it => (it.label || it.code) === label);
    return item ? item.code : label;
  };
  const getWorkItemLabelFromCode = (code, itemTypes) => {
    const item = (itemTypes || []).find(it => it.code === code);
    return item ? (item.label || item.code) : code;
  };

  // A Test Run block is told from a Test Case by its type LABEL only (both share code
  // T_CASE) — same label-first rule the backend uses.
  const isTestRunBlock = (level) => {
    const label = (level.work_item_type_label || '').toLowerCase();
    return label.includes('test run') || label.includes('testrun');
  };

  // ---- Block-tree rendering (Step 2) ----
  const childBlocksOf = (parentId) =>
    formData.levels.filter(l => (l.parent_id || 'L1') === parentId);

  // ---- Live trace-tree picture (Step 2, right-hand side) ----
  // Derived straight from formData on every render, so the diagram is never stale: the node's
  // caption is the work-item type's description (its label), which is what the level actually
  // traces. Test Run blocks are never "optional" — an un-run test is Not Executed, not Missing.
  const previewNodes = [
    {
      id: 'L1',
      parent: null,
      depth: 1,
      isRoot: true,
      label: getWorkItemLabelFromCode(formData.base_work_item, workItemTypes),
      sub: formData.base_level_name || (formData.base_filter_id ? `#${formData.base_filter_id}` : ''),
      title: 'Base level (L1)'
    },
    ...formData.levels.map((l, i) => {
      const label = getWorkItemLabelFromCode(l.work_item_type, levelWorkItemTypes[i] || workItemTypes);
      return {
        id: l.id,
        parent: l.parent_id || 'L1',
        depth: l.level_num,
        label,
        sub: l.level_name || (l.filter_id ? `#${l.filter_id}` : ''),
        optional: l.must_have === false && !isTestRunBlock(l),
        title: `L${l.level_num}: ${label || 'no type selected'}${l.must_have === false && !isTestRunBlock(l) ? ' (optional)' : ''}`
      };
    })
  ];

  // Clicking a node unfolds its block and scrolls the form to it — the diagram doubles as a
  // table of contents once the tree is deep enough to scroll.
  const focusBlock = (id) => {
    setActiveBlockId(id);
    if (id !== 'L1') {
      setCollapsedBlocks(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
    const el = document.getElementById(`block-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // The name a block is known by in the UI: its level name, else its type, else a fallback.
  const blockDisplayName = (level) => {
    if (!level) return '';
    if (level === 'L1' || level.id === 'L1') return '';
    const idx = formData.levels.findIndex(l => l.id === level.id);
    return level.level_name
      || getWorkItemLabelFromCode(level.work_item_type, levelWorkItemTypes[idx] || workItemTypes)
      || `Level ${level.level_num} block`;
  };

  const parentDisplayName = (level) => {
    const pid = level.parent_id || 'L1';
    if (pid === 'L1') {
      return formData.base_level_name
        || getWorkItemLabelFromCode(formData.base_work_item, workItemTypes)
        || 'the base level';
    }
    return blockDisplayName(formData.levels.find(l => l.id === pid));
  };

  // Blocks in tree order (depth-first), so a branch's members sit together — but each is
  // rendered as its OWN top-level card. Nesting the cards physically made an L5 block four
  // borders deep and half the width; here the L-badge + "under X" states the parenting, and
  // the diagram on the right carries the shape.
  const flatBlocks = (parentId = 'L1', depth = 2) =>
    childBlocksOf(parentId).flatMap(b => [
      { level: b, depth },
      ...flatBlocks(b.id, depth + 1)
    ]);

  const renderLevelCard = (level, depth) => {
    const i = formData.levels.findIndex(l => l.id === level.id);
    const collapsed = collapsedBlocks.has(level.id);
    const typeLabel = getWorkItemLabelFromCode(level.work_item_type, levelWorkItemTypes[i] || workItemTypes);
    const childCount = childBlocksOf(level.id).length;
    // Compact one-line summary shown when the block's settings are folded.
    const summaryBits = [];
    if (typeLabel) summaryBits.push(typeLabel);
    if (level.filter_id) summaryBits.push(`#${level.filter_id}`);
    if (level.must_have === false && !isTestRunBlock(level)) summaryBits.push('optional');
    if (childCount) summaryBits.push(`${childCount} sub-level${childCount > 1 ? 's' : ''}`);

    const isDragging = dragBlockId === level.id;
    const isDropTarget = dropParentId === level.id;

    return (
      <div
        key={level.id}
        id={`block-${level.id}`}
        className={`level-card${collapsed ? ' is-collapsed' : ''}${isDragging ? ' is-dragging' : ''}${isDropTarget ? ' is-droptarget' : ''}${activeBlockId === level.id ? ' is-active' : ''}`}
        data-drop-target={level.id}
        onFocus={() => setActiveBlockId(level.id)}
        /* A hint of depth without nesting; capped so a deep chain never eats the width */
        style={{ marginLeft: Math.min(depth - 2, 4) * 14 }}
      >
        <div className="level-card-head">
          <span
            className="block-drag-handle"
            onPointerDown={startBlockDrag(level)}
            title="Drag onto another block to move this level (and everything under it) under it"
            aria-label="Drag to move this block"
          >⠿</span>
          <span className="lvl-badge">L{level.level_num}</span>
          <button
            type="button"
            className="block-collapse-btn"
            onClick={() => toggleCollapse(level.id)}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand block settings' : 'Fold block settings'}
          >
            <span className={`block-chevron${collapsed ? ' collapsed' : ''}`}>▾</span>
            <h4>{level.level_name ? level.level_name : `Level ${level.level_num} block`}</h4>
          </button>
          <span className="block-under">under <b>{parentDisplayName(level)}</b></span>
          {collapsed && summaryBits.length > 0 && (
            <span className="block-summary">{summaryBits.join(' · ')}</span>
          )}
          {/* Remove belongs to the block's settings: a folded tree is for reading the shape,
              and a row of red buttons dominates it (and invites a mis-click on a subtree). */}
          {!collapsed && (
            <button
              onClick={() => handleRemoveLevel(i)}
              className="btn-danger btn-sm block-remove"
              title="Remove this block and everything under it"
              aria-label="Remove this block and everything under it"
            >✕</button>
          )}
        </div>

        {!collapsed && (
          <>
            <div className="field-grid">
              <div className="form-group">
                <label>Level name</label>
                <input
                  type="text"
                  value={level.level_name || ''}
                  onChange={(e) => handleLevelChange(i, 'level_name', e.target.value)}
                  placeholder={`e.g., Test Cases`}
                />
              </div>
              <div className="form-group">
                <label>Item type</label>
                <SearchableSelect
                  value={getWorkItemLabelFromCode(level.work_item_type, levelWorkItemTypes[i] || workItemTypes)}
                  onChange={(e) => handleLevelChange(i, 'work_item_type', e.target.value)}
                  options={['', ...getWorkItemLabels(levelWorkItemTypes[i] || workItemTypes)]}
                  placeholder="Search and select..."
                />
              </div>
              <div className="form-group field-wide">
                <label>Project</label>
                <SearchableSelect
                  value={(() => {
                    const p = projects.find(p => p.id === level.project_id && p.version_id === level.version_id);
                    return p ? `${p.name} (v${p.version_label})` : '';
                  })()}
                  onChange={(e) => {
                    handleLevelChange(i, 'project_id', projectValueMap[e.target.value]);
                  }}
                  options={['', ...projectOptions]}
                  placeholder="Search and select project..."
                />
              </div>
              <div className="form-group">
                <label>Filter</label>
                <FilterSelect
                  projectId={level.project_id || formData.project_id}
                  itemTypeCode={level.work_item_type}
                  itemTypeLabel={level.work_item_type_label
                    || getWorkItemLabelFromCode(level.work_item_type, levelWorkItemTypes[i] || workItemTypes)}
                  versionId={level.version_id || formData.version_id}
                  orcanosUrl={orcanosUrl}
                  value={level.filter_id}
                  onChange={(v) => handleLevelChange(i, 'filter_id', v)}
                  placeholder="e.g., 934"
                  cache={filterCache}
                  ensure={ensureFilters}
                />
              </div>
              {level.filter_id && (
                <div className="form-group field-wide">
                  <FilterColumnsPicker
                    filterId={level.filter_id}
                    itemType={level.work_item_type}
                    versionId={level.version_id || formData.version_id}
                    hidden={level.hidden_columns}
                    onChange={(v) => handleLevelChange(i, 'hidden_columns', v)}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Orphans filter</label>
                <FilterSelect
                  projectId={level.project_id || formData.project_id}
                  itemTypeCode={level.work_item_type}
                  itemTypeLabel={level.work_item_type_label
                    || getWorkItemLabelFromCode(level.work_item_type, levelWorkItemTypes[i] || workItemTypes)}
                  versionId={level.version_id || formData.version_id}
                  orcanosUrl={orcanosUrl}
                  linkLabel="orphans filter"
                  value={level.orphan_filter_id || ''}
                  onChange={(v) => handleLevelChange(i, 'orphan_filter_id', v)}
                  placeholder="optional"
                  cache={filterCache}
                  ensure={ensureFilters}
                />
                <small>A filter listing this level's untraced items — powers Orphan Mode in the graph</small>
              </div>
            </div>
            {/* A Test Run block has no "Must have": an un-run test shows the "Not Executed"
                pill, never "Missing" — execution isn't a coverage gap. */}
            {!isTestRunBlock(level) && (
              <div className="form-group must-have-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={level.must_have !== false}
                    onChange={(e) => handleLevelChange(i, 'must_have', e.target.checked)}
                  />
                  <span>Must have</span>
                </label>
                <small>
                  Show a ⚠ Missing indicator when an item doesn't trace to this level. When several
                  blocks share a level, tracing to any one of the must-have blocks is enough.
                </small>
              </div>
            )}
            <div className="level-card-actions">
              {level.level_num < 6 && (
                <button onClick={() => handleAddBlock(level.id)} className="btn-secondary btn-sm">＋ Level below</button>
              )}
              <button onClick={() => handleAddBlock(level.parent_id || 'L1')} className="btn-secondary btn-sm">＋ Block here</button>
            </div>
          </>
        )}
      </div>
    );
  };

  const draggedBlock = dragBlockId && formData.levels.find(l => l.id === dragBlockId);

  return (
    /* Step 2 is two columns (form + live tree), so the page widens for that step only —
       Steps 1 and 3 stay the original 600px single column. */
    <div className={`config-page${step === 2 ? ' is-wide' : ''}`}>
      {/* Our own drag ghost — a small chip, never a snapshot of the card. pointer-events:none
          so it can't shadow the drop zone under the pointer. */}
      {draggedBlock && dragPos && (
        <div className="block-drag-ghost" style={{ left: dragPos.x + 12, top: dragPos.y + 12 }}>
          ⠿ {draggedBlock.level_name || getWorkItemLabelFromCode(draggedBlock.work_item_type, workItemTypes) || 'Block'}
        </div>
      )}
      <div className="config-card">
        <div className="config-header">
          <h2>{isEditMode ? 'Edit Panel' : 'Create Panel'}</h2>
          <button onClick={handleCancel} className="btn-cancel">✕</button>
        </div>

        {step === 1 && (
          <div className="step">
            <h3>Step 1: Base Configuration</h3>

            {/* Project Selection */}
            <div className="form-group">
              <label>Project: *</label>
              <SearchableSelect
                value={(() => {
                  const p = projects.find(p => p.id === formData.project_id && p.version_id === formData.version_id);
                  return p ? `${p.name} (v${p.version_label})` : '';
                })()}
                onChange={(e) => {
                  handleProjectChange(projectValueMap[e.target.value]);
                }}
                options={['', ...projectOptions]}
                placeholder="Search and select project..."
                disabled={loadingProjects}
              />
            </div>

            {/* Level 1 Name */}
            {formData.project_id && (
              <div className="form-group">
                <label>Level Name (optional):</label>
                <input
                  type="text"
                  value={formData.base_level_name}
                  onChange={(e) => setFormData({ ...formData, base_level_name: e.target.value })}
                  placeholder="e.g., User Requirements"
                />
                <small>Display name for the first level (defaults to "L1")</small>
              </div>
            )}

            {/* Work Item Type Selection */}
            {formData.project_id && (
              <div className="form-group">
                <label>Work Item Type: *</label>
                <SearchableSelect
                  value={getWorkItemLabelFromCode(formData.base_work_item, workItemTypes)}
                  onChange={handleWorkItemChange}
                  options={['', ...getWorkItemLabels(workItemTypes)]}
                  placeholder="Search and select work item type..."
                />
              </div>
            )}

            {/* Filter ID */}
            {formData.base_work_item && (
              <div className="form-group">
                <label>Filter for {getWorkItemLabelFromCode(formData.base_work_item, workItemTypes)}: *</label>
                <FilterSelect
                  projectId={formData.project_id}
                  itemTypeCode={formData.base_work_item}
                  itemTypeLabel={getWorkItemLabelFromCode(formData.base_work_item, workItemTypes)}
                  versionId={formData.version_id}
                  orcanosUrl={orcanosUrl}
                  value={formData.base_filter_id}
                  onChange={(v) => setFormData({
                    ...formData,
                    base_filter_id: v,
                    // A new filter has its own columns: start again from "show all".
                    base_hidden_columns: String(v) === String(formData.base_filter_id) ? formData.base_hidden_columns : []
                  })}
                  placeholder="e.g., 839"
                  cache={filterCache}
                  ensure={ensureFilters}
                />
                <small>Pick a filter, or enter its ID from Orcanos</small>
                <FilterColumnsPicker
                  filterId={formData.base_filter_id}
                  itemType={formData.base_work_item}
                  versionId={formData.version_id}
                  hidden={formData.base_hidden_columns}
                  onChange={(v) => setFormData({ ...formData, base_hidden_columns: v })}
                />
              </div>
            )}

            {/* Orphans Filter ID (optional) */}
            {formData.base_work_item && (
              <div className="form-group">
                <label>Orphans Filter (optional):</label>
                <FilterSelect
                  projectId={formData.project_id}
                  itemTypeCode={formData.base_work_item}
                  itemTypeLabel={getWorkItemLabelFromCode(formData.base_work_item, workItemTypes)}
                  versionId={formData.version_id}
                  orcanosUrl={orcanosUrl}
                  linkLabel="orphans filter"
                  value={formData.base_orphan_filter_id}
                  onChange={(v) => setFormData({ ...formData, base_orphan_filter_id: v })}
                  placeholder="e.g., 1042"
                  cache={filterCache}
                  ensure={ensureFilters}
                />
                <small>A filter listing this level's untraced items — powers Orphan Mode in the graph</small>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="button-group">
              <button onClick={handleCancel} className="btn-secondary">Cancel</button>
              <button
                onClick={() => setStep(2)}
                disabled={!formData.project_id || !formData.base_work_item || !formData.base_filter_id}
                className="btn-primary"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="step step2-layout">
            <div className="step2-form">
            <h3>Step 2: Trace Levels</h3>
            <p style={{ color: '#666', marginBottom: '12px' }}>
              Build the trace tree. Add a level <b>below</b> a block to go deeper, or add
              another block <b>at the same level</b> when an item traces to more than one
              type (e.g. an L1 requirement tracing to both Software Requirements and Risks).
            </p>

            {formData.levels.length > 0 && (
              <div className="tree-tools">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setCollapsedBlocks(new Set(formData.levels.map(l => l.id)))}
                >Fold all</button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setCollapsedBlocks(new Set())}
                >Unfold all</button>
              </div>
            )}

            {formData.levels.length > 1 && (
              <p className="tree-hint">Drag a block by its ⠿ handle onto another block to move it (with its sub-levels) under that block.</p>
            )}

            {/* Base (L1) root — also a drop target, to move a block back to the top level */}
            <div
              id="block-L1"
              className={`level-root${dropParentId === 'L1' ? ' is-droptarget' : ''}${activeBlockId === 'L1' ? ' is-active' : ''}`}
              data-drop-target="L1"
            >
              <div className="level-card-head">
                <h4>L1: {formData.base_level_name || getWorkItemLabelFromCode(formData.base_work_item, workItemTypes) || 'Base level'}</h4>
              </div>
              <button onClick={() => handleAddBlock('L1')} className="btn-secondary btn-sm">＋ Add level below</button>
            </div>

            {childBlocksOf('L1').length === 0 && (
              <p style={{ color: '#999', margin: '16px 0' }}>No additional levels yet. Add one to build the trace path.</p>
            )}

            {/* One flat stack in tree order — every block is its own card, never inside another */}
            <div className="block-stack">
              {flatBlocks().map(({ level, depth }) => renderLevelCard(level, depth))}
            </div>

            <div className="button-group" style={{ marginTop: '30px' }}>
              <button onClick={handleCancel} className="btn-secondary">Cancel</button>
              <button onClick={() => setStep(1)} className="btn-secondary">← Back</button>
              <button onClick={() => setStep(3)} className="btn-primary">Next →</button>
            </div>
            </div>

            <BlockTreePreview
              nodes={previewNodes}
              activeId={activeBlockId}
              onSelect={focusBlock}
            />
          </div>
        )}

        {step === 3 && (
          <div className="step">
            <h3>Step 3: Panel Details & Privacy</h3>
            <div className="form-group">
              <label>Panel Name:</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Req → Design → Test"
              />
            </div>
            <div className="form-group">
              <label>Privacy:</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    checked={formData.is_public}
                    onChange={() => setFormData({ ...formData, is_public: true })}
                  />
                  🌐 Public (all users can see)
                </label>
                <label>
                  <input
                    type="radio"
                    checked={!formData.is_public}
                    onChange={() => setFormData({ ...formData, is_public: false })}
                  />
                  🔒 Private (only me)
                </label>
              </div>
            </div>
            <div className="form-group">
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={formData.owner_only_edit}
                  onChange={(e) => setFormData({ ...formData, owner_only_edit: e.target.checked })}
                />
                Only owner can edit
              </label>
              <small className="field-hint">
                {formData.owner_only_edit
                  ? 'Only you can edit or delete this panel.'
                  : 'Anyone in your account can edit this public panel.'}
              </small>
            </div>

            {isEditMode && (
              <div className="clone-panel">
                {!cloneOpen ? (
                  <>
                    <button
                      type="button"
                      className="btn-secondary clone-open-btn"
                      onClick={() => {
                        setCloneName(`Copy of ${formData.name || 'panel'}`);
                        setCloneOpen(true);
                      }}
                    >
                      ⧉ Clone this panel
                    </button>
                    <small className="field-hint">
                      Saves the settings currently on screen as a new panel. This panel is left unchanged.
                    </small>
                  </>
                ) : (
                  <>
                    <label>New panel name:</label>
                    <input
                      type="text"
                      value={cloneName}
                      onChange={(e) => setCloneName(e.target.value)}
                      placeholder="e.g., Copy of Req → Design → Test"
                      autoFocus
                    />
                    <div className="clone-actions">
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleClone}
                        disabled={cloning || loading}
                      >
                        {cloning ? 'Creating copy...' : '⧉ Create copy'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setCloneOpen(false)}
                        disabled={cloning}
                      >
                        Cancel
                      </button>
                    </div>
                    <small className="field-hint">
                      The copy gets everything on screen — levels, filters, privacy — and belongs to you.
                      This panel is left unchanged.
                    </small>
                  </>
                )}
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="button-group">
              <button onClick={handleCancel} className="btn-secondary">Cancel</button>
              <button onClick={() => setStep(2)} className="btn-secondary">← Back</button>
              <button onClick={handleSave} disabled={loading || cloning} className="btn-primary">
                {loading ? 'Saving...' : isEditMode ? 'Update Panel' : 'Save Panel'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfigPage;
